import { z } from "zod";
import { MatchType, Rule, Transaction } from "../domain.js";
import { Id } from "../primitives.js";

/**
 * Preview before commit is the rule for anything that rewrites history, which
 * is why `preview` is a first-class endpoint rather than a UI trick.
 */

export const RuleDraft = z.object({
  /** null, or absent, means any account. */
  accountId: Id.nullable().optional(),
  matchType: MatchType.default("contains"),
  pattern: z.string().trim().min(1).max(200),
  categoryId: Id,
  personId: Id.nullable().optional(),
  /** Lower wins. A rule for "TESCO PETROL" must outrank one for "TESCO". */
  priority: z.number().int().min(0).max(100_000).default(1000),
});

export const RuleCreate = RuleDraft.extend({
  /**
   * Off by default. Creating a rule and restating six months of history are
   * two different decisions and the UI asks for both.
   */
  applyToExisting: z.boolean().default(false),
});

export const RulePatch = z
  .object({
    accountId: Id.nullable(),
    matchType: MatchType,
    pattern: z.string().trim().min(1).max(200),
    categoryId: Id,
    personId: Id.nullable(),
    priority: z.number().int().min(0).max(100_000),
    enabled: z.boolean(),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, "Nothing to change.");

export const RuleChange = z.object({
  transaction: Transaction,
  fromCategory: z.string(),
  toCategory: z.string(),
});

/**
 * What the rule would do, said plainly enough to put in a confirmation:
 * "34 transactions will change. 3 you categorised by hand are left alone."
 */
export const RulePreview = z.object({
  matched: z.number().int(),
  willChange: z.number().int(),
  /** Already filed where the rule would put them. */
  alreadyCorrect: z.number().int(),
  /** Matched, but categorised by a human — so left exactly as they are. */
  protectedManual: z.number().int(),
  /** A bounded sample, most recent first. `willChange` is the real count. */
  changes: z.array(RuleChange),
  truncated: z.boolean(),
});

export const RuleList = z.object({ data: z.array(Rule) });

export const RuleWriteResult = z.object({
  rule: Rule,
  applied: z.number().int(),
});

/** Whole-ledger reapply, same preview-then-commit path. */
export const RuleReapply = z.object({ commit: z.boolean().default(false) });

export const RuleReapplyResult = z.object({
  changed: z.number().int(),
  committed: z.boolean(),
  byRule: z.array(z.object({ ruleId: Id, pattern: z.string(), changed: z.number().int() })),
});

export type RuleDraft = z.infer<typeof RuleDraft>;
export type RuleCreate = z.infer<typeof RuleCreate>;
export type RulePatch = z.infer<typeof RulePatch>;
export type RuleChange = z.infer<typeof RuleChange>;
export type RulePreview = z.infer<typeof RulePreview>;
export type RuleList = z.infer<typeof RuleList>;
export type RuleWriteResult = z.infer<typeof RuleWriteResult>;
export type RuleReapply = z.infer<typeof RuleReapply>;
export type RuleReapplyResult = z.infer<typeof RuleReapplyResult>;
